import type {
	CellClassParams,
	ColDef,
	GetRowIdParams,
	GridApi,
	RowHeightParams,
} from "ag-grid-community";
import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import { Box } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	panelGridSx,
	ROW_LINE,
	SectionPanel,
	TEXT,
} from "@/controls/section-panel/SectionPanel";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import type { StandData } from "../models/StandData";
import { StandCellRenderer } from "./StandCellRenderer";

const STAND_PROPERTIES = [
	{ key: "selWork", label: "Sel / Work" },
	{ key: "tc", label: "T C" },
	{ key: "cartridge", label: "Cartridge" },
	{ key: "barrel", label: "Barrel" },
	{ key: "pass", label: "Pass" },
	{ key: "rollDiam", label: "Roll Diam" },
	{ key: "rollGroove", label: "Roll Groove" },
	{ key: "grooveRecipe", label: "Groove Recipe" },
	{ key: "passCount", label: "Pass Count ABB" },
	{ key: "rFactor", label: "R-Factor" },
] as const;

interface StandGridRow {
	property: string;
	propertyKey: string;
	[standName: string]: StandData | string;
}

const standCellStyle = (params: CellClassParams) => {
	const base = {
		overflow: "hidden",
		textOverflow: "ellipsis",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	};
	const s = params.value as StandData | undefined;
	if (!s || typeof s === "string") return base;
	if (s.Selected && !s.BarrelId) return { ...base, backgroundColor: "#F7A2A2" };
	if (!s.Selected && !s.Working) return { ...base, backgroundColor: "#D6D4D4" };
	return base;
};

const standCellClass = (params: CellClassParams): string => {
	const key = params.data?.propertyKey as string;
	const s = params.value as StandData | undefined;
	if (!s || typeof s === "string") return "stand-cell";

	if (key === "cartridge" && s.HasRollset)
		return "stand-cell stand-cell-editable";
	if (key === "pass" && s.HasPass && s.PassName && s.CanChangePass)
		return "stand-cell stand-cell-editable";

	return "stand-cell";
};

const HEADER_HEIGHT = 26;

const baseRowHeight = (key: string) => {
	if (key === "rFactor") return 38;
	if (key === "barrel" || key === "pass") return 34;
	return 26;
};

/**
 * Row heights stretched by `scale` so the matrix fills its panel. Rounded on the running
 * total, so the rows add up to the stretched height without a leftover strip.
 */
const scaledRowHeights = (scale: number) => {
	const heights: Record<string, number> = {};
	let total = 0;
	let placed = 0;
	for (const { key } of STAND_PROPERTIES) {
		total += baseRowHeight(key) * scale;
		heights[key] = Math.round(total) - placed;
		placed += heights[key];
	}
	return heights;
};

const BASE_ROWS_HEIGHT = STAND_PROPERTIES.reduce(
	(sum, { key }) => sum + baseRowHeight(key),
	0,
);
const NATURAL_HEIGHT = HEADER_HEIGHT + BASE_ROWS_HEIGHT + 2;

const getRowId = (params: GetRowIdParams) => params.data.propertyKey;

/** A rule between stands, so each column reads as one stand from top to bottom. */
const standGridSx = {
	"& .ag-cell.stand-cell, & .ag-header-cell:not([col-id=property])": {
		borderLeft: `1px solid ${ROW_LINE} !important`,
	},
} as const;

const buildRowData = (stands: StandData[]): StandGridRow[] => {
	return STAND_PROPERTIES.map((prop) => {
		const row: StandGridRow = {
			property: prop.label,
			propertyKey: prop.key,
		};
		for (const stand of stands) {
			row[stand.Name] = stand;
		}
		return row;
	});
};

const buildColumnDefs = (standNames: string[]): ColDef[] => {
	const propertyCol: ColDef = {
		field: "property",
		headerName: "",
		pinned: "left",
		width: 110,
		minWidth: 110,
		maxWidth: 110,
		suppressMovable: true,
		sortable: false,
		filter: false,
		resizable: false,
		cellStyle: {
			...TEXT.head,
			display: "flex",
			alignItems: "center",
			justifyContent: "flex-start",
			textAlign: "left",
		},
		cellClass: "stand-property-cell",
		wrapText: false,
		autoHeight: false,
	};

	const standCols: ColDef[] = standNames.map((name) => ({
		colId: name,
		headerName: name,
		valueGetter: (params) => params.data?.[name],
		flex: 1,
		minWidth: 85,
		sortable: false,
		filter: false,
		resizable: false,
		cellRenderer: StandCellRenderer,
		cellStyle: standCellStyle,
		cellClass: standCellClass,
		wrapText: false,
		autoHeight: false,
	}));

	return [propertyCol, ...standCols];
};

const defaultColDef: ColDef = {
	...globalDefaultColDef,
	resizable: false,
};

interface StandsGridProps {
	stands: StandData[];
	title: string;
	onChangePass?: (stand: StandData) => void;
	onChangeRollset?: (stand: StandData) => void;
	writeDisabled?: boolean;
}

/** Renders a single stand group (Roughing or Finishing) as an AG Grid */
export const StandsGrid = ({
	stands,
	title,
	onChangePass,
	onChangeRollset,
	writeDisabled = false,
}: StandsGridProps) => {
	const rowData = useMemo(() => buildRowData(stands), [stands]);

	const namesKey = stands.map((s) => s.Name).join(",");
	const colDefs = useMemo(
		() => buildColumnDefs(stands.map((s) => s.Name)),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[namesKey],
	);

	const context = useMemo(
		() => ({ onChangePass, onChangeRollset, writeDisabled }),
		[onChangePass, onChangeRollset, writeDisabled],
	);

	// The two matrices share the height left under the header; their rows grow to fill it.
	const boxRef = useRef<HTMLDivElement>(null);
	const apiRef = useRef<GridApi | null>(null);
	const [scale, setScale] = useState(1);

	// Cell renderers read the lock from the context; redraw them when it flips.
	useEffect(() => {
		apiRef.current?.refreshCells({ force: true });
	}, [writeDisabled]);
	const hasStands = stands.length > 0;

	useEffect(() => {
		const box = boxRef.current;
		if (!hasStands || !box) return;
		const observer = new ResizeObserver(() => {
			const free = box.clientHeight - NATURAL_HEIGHT + BASE_ROWS_HEIGHT;
			const next = Math.max(1, free / BASE_ROWS_HEIGHT);
			setScale((prev) => (Math.abs(prev - next) < 0.005 ? prev : next));
		});
		observer.observe(box);
		return () => observer.disconnect();
	}, [hasStands]);

	// The size can be measured before the grid exists; then the reset below finds no grid
	// and the rows kept their base height. The heights live in a ref and are applied
	// both when the scale changes and when the grid becomes ready, whichever comes last.
	const rowHeightsRef = useRef(scaledRowHeights(1));
	const getRowHeightCb = useCallback(
		(params: RowHeightParams) =>
			rowHeightsRef.current[params.data?.propertyKey] ?? HEADER_HEIGHT,
		[],
	);

	useEffect(() => {
		rowHeightsRef.current = scaledRowHeights(scale);
		apiRef.current?.resetRowHeights();
	}, [scale]);

	if (!hasStands) return null;

	return (
		<SectionPanel
			title={title}
			icon={<ViewWeekOutlinedIcon />}
			flush
			sx={{ height: "100%" }}
		>
			<Box
				ref={boxRef}
				sx={{ position: "relative", height: "100%", minHeight: NATURAL_HEIGHT }}
			>
				{/* Out of the flow, so stretched rows never hold the panel open when the window shrinks */}
				<Box
					sx={[panelGridSx, standGridSx, { position: "absolute", inset: 0 }]}
				>
					<AgGridReact
						rowData={rowData}
						columnDefs={colDefs}
						defaultColDef={defaultColDef}
						context={context}
						domLayout="autoHeight"
						headerHeight={HEADER_HEIGHT}
						onGridReady={(e) => {
							apiRef.current = e.api;
							e.api.resetRowHeights();
						}}
						getRowHeight={getRowHeightCb}
						getRowId={getRowId}
						suppressCellFocus
						suppressContextMenu
						suppressColumnVirtualisation
						suppressHorizontalScroll
						suppressRowHoverHighlight
						animateRows={false}
					/>
				</Box>
			</Box>
		</SectionPanel>
	);
};
